import React, { useCallback, useEffect, useState } from 'react';
import './App.scss';
import axios from 'axios';
import { useAuth0 } from "@auth0/auth0-react";
import Loading from './Components/Loading/Loading';
import LoginError from './Components/Login/LoginError';
import REACT_APP_API_URL from "./Api/ApiEnvironment";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConfig, fetchResellerConfig, selectConfig, selectResellerConfig } from './Store/configSlice';
import { AppDispatch } from './Store/store';
import { selectUserId } from './Store/adminSlice';
import { fetchTrainingDocuments } from './Store/trainingSlice';
import useApi from './Api/ApiHook';
import ApiContext from './Api/ApiContext';
import useEvents from './Api/ApiEvents/ApiEvents';
import { creditSlice } from './Store/creditSlice';
import useAnalytics from './Analytics/Analytics';
import { ToastProvider } from './Components/Toast/Toast';
import Consent from './Components/Consent/Consent';
import RouterOrchestrator from './Core/RouterOrchetrator';
import SidebarOrchastrator from './Components/Sidebar/SidebarOrchestrator';
import { EPath, Paths } from './Core/EPath';
import { useLocation } from 'react-router-dom';
import ReadOnlyBanner from './Components/Banner/ReadOnlyBanner';

import { Userpilot } from 'userpilot'

const App = () => {

  Userpilot.initialize('NX-b6293e97');

  const { isLoading, error, isAuthenticated, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [ loading, setLoading ] = useState(true);
  const [ darkMode, setDarkMode ] = useState(true);
  const { i18n } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const refreshInterval = setInterval(() => {
      if(process.env.REACT_APP_IS_RESELLER)
        window.location.reload();
    }, 3000000);

    return () => clearInterval(refreshInterval);
  }, []);

  const refresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const dispatch = useDispatch<AppDispatch>();
  
  const api = useApi();
  const location = useLocation();

  const [isApiReady, setApiReady] = useState(false);

  const { trackEvent, EventTypes, handleClickEvent } = useAnalytics();

  useEffect(() => {
    document.addEventListener('click', handleClickEvent);
    return () => document.removeEventListener('click', handleClickEvent);
  }, [trackEvent, handleClickEvent]);

  useEvents({api, userId: user?.sub});

  useEffect(() => {
    if (api) {
        setApiReady(true);
        if (!process.env.REACT_APP_IS_RESELLER) {
          dispatch(fetchTrainingDocuments(api));
        }
    }
  }, [api, dispatch]);
  
  useEffect(() => {
    if (isAuthenticated && user) {
      getAccessTokenSilently().then(token => {
        dispatch(fetchConfig(token));
      });
      
      const lastLoginTrack = localStorage.getItem('lastLoginTrack');
      const today = new Date().toISOString().split('T')[0];
      const shouldTrackLogin = !lastLoginTrack || new Date(lastLoginTrack).toISOString().split('T')[0] !== today;
  
      if (shouldTrackLogin) {
        trackEvent(EventTypes.LOGIN);
        localStorage.setItem('lastLoginTrack', new Date().toISOString());
      }
    }
  }, [isAuthenticated, user, dispatch, refreshKey, getAccessTokenSilently]);

  const userId = useSelector(selectUserId);

  // Reseller config

  useEffect(() => {
    if (isAuthenticated && process.env.REACT_APP_IS_RESELLER) {
      getAccessTokenSilently().then(token => {
        const type = Paths.isActivePath(location, [EPath.REVIEW_ANONYMISATION, EPath.REVIEW_DETAIL, EPath.REVIEW_COPILOT, ]) ? "review" : "draft";
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const resellerDocumentId = pathSegments[1];
        dispatch(fetchResellerConfig({ token, type, resellerDocumentId: resellerDocumentId }));
      });
    }
  }, [isAuthenticated, dispatch, getAccessTokenSilently]);

  const config = useSelector(selectConfig);

  const resellerConfig = useSelector(selectResellerConfig);

  // End reseller config

  useEffect(() => {
    if (isAuthenticated && config?.user) {

      getAccessTokenSilently().then(token => {
        axios.post(REACT_APP_API_URL + '/login', 
        { user },
        { headers: { Authorization: `Bearer ${token}` } }
        ).then((response) => {
          dispatch(creditSlice.actions.setCreditAmount(response?.data.credits));

          if(process.env.REACT_APP_IS_RESELLER) {
            i18n.changeLanguage(resellerConfig?.language);
            setDarkMode(false);
          } else {
            switch(response?.data.language) {
              case "Nederlands":
                i18n.changeLanguage("nl");
                break;
              case "Français":
                i18n.changeLanguage("fr");
                break;
              case "English":
                i18n.changeLanguage("en");
                break;
              case "Deutsch":
                i18n.changeLanguage("de");
                break;
              default:
                i18n.changeLanguage("en");
                break;
            }

            setDarkMode(response?.data.darkMode)
          }

          Userpilot.identify(
            user.sub, 
            {
              name: user.nickname, 
              email: user.email,
            }
          );

          setLoading(false);
        });
      })
    }
  }, [isAuthenticated, user, getAccessTokenSilently, i18n, resellerConfig, dispatch, config]);

  useEffect(() => {
    if(!isAuthenticated && !isLoading) loginWithRedirect({
      appState: { returnTo: location.pathname + location.search }
    });
  }, [isAuthenticated, isLoading]);

  const [ showConsent, setShowConsent ] = useState(false);

  useEffect(() => {
    if(isLoading || loading || !isAuthenticated) return;
    setShowConsent(true);
  }, [isLoading, loading, isAuthenticated]);

  if(!isApiReady) return <Loading />;

  if (error) {
    return <LoginError />;
  }

  if (isLoading || loading || !isAuthenticated) {
    return <Loading />;
  }

  return (
    <ApiContext.Provider value={api}>
      <ToastProvider>
        <div className={"App " + (darkMode ? "dark" : "light") + (userId ? " impersonate" : "")} id="app" key={refreshKey}>
          <ReadOnlyBanner />
          <div className="main">
            <SidebarOrchastrator />
            {showConsent && <Consent />}
            <div className="content">
              <RouterOrchestrator darkMode={darkMode} setDarkMode={setDarkMode} refresh={refresh} />
            </div>
          </div>
        </div>
      </ToastProvider>
    </ApiContext.Provider>
  );
}

export default App;
